import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiMail, FiPlus, FiSend, FiShield, FiTrash2 } from "react-icons/fi";
import "../../styles/DashboardHome.css";
import "../../styles/TeamApprovals.css";
import {
  createClientTeamApprover,
  deleteClientTeamApprover,
  fetchClientTeamApprovers,
  sendClientTeamApproverInvitation,
  updateClientTeamApprover,
} from "../../lib/api";

const EMPTY_APPROVER = { username: "", display_name: "", email: "", approval_modules: [] };

export default function TeamApprovals() {
  const isOwner = String(localStorage.getItem("role") || "").trim().toLowerCase() === "client_ems_owner";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [modules, setModules] = useState({});
  const [form, setForm] = useState(EMPTY_APPROVER);

  const loadApprovers = async () => {
    try {
      setLoading(true);
      const data = await fetchClientTeamApprovers();
      setApprovers(Array.isArray(data?.approvers) ? data.approvers : []);
      setModules(data?.available_modules && typeof data.available_modules === "object" ? data.available_modules : {});
    } catch (err) {
      toast.error(err?.message || "Failed to load client approvers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) loadApprovers();
  }, [isOwner]);

  const toggleFormModule = (moduleKey) => {
    setForm((prev) => {
      const selected = new Set(prev.approval_modules || []);
      if (selected.has(moduleKey)) selected.delete(moduleKey);
      else selected.add(moduleKey);
      return { ...prev, approval_modules: Array.from(selected) };
    });
  };

  const createApprover = async (event) => {
    event.preventDefault();
    if (!form.username.trim() || !form.email.trim() || !(form.approval_modules || []).length) {
      toast.error("Username, account email and at least one module are required");
      return;
    }
    try {
      setSaving(true);
      await createClientTeamApprover(form);
      setForm(EMPTY_APPROVER);
      toast.success("Client approver created and invitation sent");
      await loadApprovers();
    } catch (err) {
      toast.error(err?.message || "Failed to create client approver");
    } finally {
      setSaving(false);
    }
  };

  const toggleApproverModule = async (approver, moduleKey) => {
    const selected = new Set(approver.approval_modules || []);
    if (selected.has(moduleKey)) selected.delete(moduleKey);
    else selected.add(moduleKey);
    if (selected.size === 0) {
      toast.error("An approver must retain at least one module");
      return;
    }
    try {
      setSaving(true);
      await updateClientTeamApprover(approver.username, { approval_modules: Array.from(selected) });
      await loadApprovers();
    } catch (err) {
      toast.error(err?.message || "Failed to update approval modules");
    } finally {
      setSaving(false);
    }
  };

  const setEmail = async (approver) => {
    const email = window.prompt(`Set the account email for ${approver.username}:`, approver.email || "");
    if (email === null) return;
    if (!email.trim()) {
      toast.error("Account email cannot be empty");
      return;
    }
    try {
      setSaving(true);
      await updateClientTeamApprover(approver.username, { email });
      toast.success("Account email updated");
      await loadApprovers();
    } catch (err) {
      toast.error(err?.message || "Failed to update account email");
    } finally {
      setSaving(false);
    }
  };

  const sendInvitation = async (approver) => {
    try {
      setSaving(true);
      await sendClientTeamApproverInvitation(approver.username);
      toast.success("Invitation email sent");
    } catch (err) {
      toast.error(err?.message || "Failed to send invitation email");
    } finally {
      setSaving(false);
    }
  };

  const removeApprover = async (approver) => {
    if (!window.confirm(`Remove approval access for ${approver.username}?`)) return;
    try {
      setSaving(true);
      await deleteClientTeamApprover(approver.username);
      toast.success("Client approver removed");
      await loadApprovers();
    } catch (err) {
      toast.error(err?.message || "Failed to remove client approver");
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="dashboard-page team-approvals-page">
        <div className="dashboard-header"><div><h1>Team & Approvals</h1><p className="subtitle">This page is available to the nominated Client EMS Owner.</p></div></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page team-approvals-page">
      <div className="dashboard-header">
        <div>
          <p className="team-approvals-eyebrow">Client EMS Owner</p>
          <h1>Team & Approvals</h1>
          <p className="subtitle">Choose the people who can approve individual EMS modules for your organisation.</p>
        </div>
      </div>

      <section className="card team-approvals-guidance">
        <FiShield aria-hidden="true" />
        <div><strong>Keep approval independent</strong><span>Assign only the modules each person is responsible for. Approvers cannot approve their own changes, and every decision is recorded in the EMS audit trail.</span></div>
      </section>

      <section className="card team-approvals-card">
        <h2>Add client approver</h2>
        <form onSubmit={createApprover} className="team-approver-form">
          <label><span>Username</span><input value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} placeholder="e.g. jane.smith" disabled={saving} /></label>
          <label><span>Display name</span><input value={form.display_name} onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))} placeholder="e.g. Jane Smith" disabled={saving} /></label>
          <label><span>Account email</span><input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="e.g. jane.smith@example.com" disabled={saving} /></label>
          <fieldset><legend>Modules this person may approve</legend><div className="team-module-grid">
            {Object.entries(modules).map(([key, label]) => <label key={key} className="team-module-option"><input type="checkbox" checked={(form.approval_modules || []).includes(key)} onChange={() => toggleFormModule(key)} disabled={saving} /><span>{label}</span></label>)}
          </div></fieldset>
          <button className="btn-primary" type="submit" disabled={saving || loading}><FiPlus /> {saving ? "Saving..." : "Add approver"}</button>
        </form>
      </section>

      <section className="card team-approvals-card">
        <h2>Current client approvers</h2>
        {loading ? <p className="team-empty">Loading approvers...</p> : null}
        {!loading && approvers.length === 0 ? <p className="team-empty">No client approvers have been assigned yet.</p> : null}
        <div className="team-approver-list">
          {approvers.map((approver) => <article key={approver.username} className="team-approver-row">
            <div className="team-approver-identity"><strong>{approver.display_name || approver.username}</strong>{approver.display_name ? <span>{approver.username}</span> : null}<span>{approver.email || "No email recorded"}</span></div>
            <div className="team-module-grid compact">
              {Object.entries(modules).map(([key, label]) => <label key={key} className="team-module-option"><input type="checkbox" checked={(approver.approval_modules || []).includes(key)} onChange={() => toggleApproverModule(approver, key)} disabled={saving} /><span>{label}</span></label>)}
            </div>
            <div className="team-approver-actions"><button type="button" className="btn-secondary" onClick={() => setEmail(approver)} disabled={saving}><FiMail /> Set email</button><button type="button" className="btn-secondary" onClick={() => sendInvitation(approver)} disabled={saving || !approver.email} title={approver.email ? "Send a new set-password link" : "Set an email first"}><FiSend /> Send invite</button><button type="button" className="btn-danger" onClick={() => removeApprover(approver)} disabled={saving}><FiTrash2 /> Remove</button></div>
          </article>)}
        </div>
      </section>
    </div>
  );
}
